import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMatrixClient } from '../../../../context/MatrixContext';
import { useModal } from '../../../../context/ModalContext';
import { useI18n } from '../../../../context/I18nContext';
import { useAvatar } from '../../../../context/AvatarContext';
import { useSettings } from '../../../../context/SettingsContext';
import { useDisambiguation } from '../../../../context/DisambiguationContext';
import matrixService from '../../../../services/matrixService';
import logger from '../../../../utils/logger';
import './InviteView.css';

const INVITER_AVATAR_SIZE = 96;

/**
 * InviteView - Inline invite acceptance UI shown in place of chat messages
 */
const InviteView = ({ roomId, onAccepted, onDeclined }) => {
  const client = useMatrixClient();
  const { showError } = useModal();
  const { t } = useI18n();
  const { getUserAvatar } = useAvatar();
  const { settings, updateSetting } = useSettings();
  const { needsDisambiguation } = useDisambiguation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [inviterAvatarUrl, setInviterAvatarUrl] = useState(null);

  // Get invite information from the room
  const room = client?.getRoom(roomId);
  const myUserId = client?.getUserId();

  let inviterUserId = null;
  let inviterDisplayName = null;
  let inviterBaseName = null;
  let isDM = false;

  if (room && myUserId) {
    const memberEvent = room.currentState.getStateEvents('m.room.member', myUserId);

    if (memberEvent) {
      isDM = memberEvent.getContent().is_direct === true;
      inviterUserId = memberEvent.getSender();
      const inviterMember = room.getMember(inviterUserId);
      inviterBaseName = inviterMember?.rawDisplayName || inviterMember?.name || inviterUserId;
      inviterDisplayName = (inviterUserId && needsDisambiguation(inviterUserId))
        ? `${inviterBaseName} (${inviterUserId})`
        : inviterBaseName;
    }
  }

  // Load inviter avatar as blob
  useEffect(() => {
    if (!inviterUserId || !inviterBaseName) {
      return;
    }

    let mounted = true;

    const loadAvatar = async () => {
      try {
        const avatarUrl = await getUserAvatar(inviterUserId, inviterBaseName, INVITER_AVATAR_SIZE);
        if (mounted) {
          setInviterAvatarUrl(avatarUrl);
        }
      } catch (error) {
        logger.warn('Failed to load inviter avatar:', error);
      }
    };

    loadAvatar();

    return () => {
      mounted = false;
    };
  }, [inviterUserId, inviterBaseName, getUserAvatar]);

  const handleAccept = async () => {
    setIsProcessing(true);

    try {
      // Add to pending DM cache if this is a DM invite (before joining)
      if (isDM) {
        matrixService.addPendingDMRoom(roomId);
      }

      // Join the room
      await client.joinRoom(roomId);

      // Update m.direct for DM invites
      if (isDM && inviterUserId) {
        try {
          await matrixService.setDMRoom(roomId, inviterUserId);
          logger.debug(`Updated m.direct for DM invite from ${inviterUserId}`);
        } catch (dmError) {
          logger.error('Failed to update m.direct, but room was joined successfully:', dmError);
        }
      }

      logger.info(`Successfully accepted invite to room ${roomId}`);

      if (onAccepted) {
        onAccepted(roomId);
      }
    } catch (error) {
      logger.error('Failed to accept invite:', error);

      // Clean up pending DM if join failed
      if (isDM) {
        matrixService.removePendingDMRoom(roomId);
      }

      const errcode = error?.errcode || error?.data?.errcode;

      // M_NOT_FOUND (404) or M_FORBIDDEN (403) typically means room is no longer available
      // M_NOT_FOUND: Room doesn't exist or inviter has left (DM rooms are destroyed when creator leaves)
      // M_FORBIDDEN: User is not allowed to join (invite was revoked)
      if (errcode === 'M_NOT_FOUND' || errcode === 'M_FORBIDDEN' || error?.httpStatus === 404 || error?.httpStatus === 403) {
        // Auto-leave to remove the stale invite from list
        try {
          await client.leave(roomId);
          logger.info(`Auto-left room ${roomId} after failed join (inviter left)`);
        } catch (leaveError) {
          logger.warn('Failed to auto-leave unavailable room:', leaveError);
        }

        showError(
          t('InviteModal.roomNoLongerAvailableTitle'),
          t('InviteModal.roomNoLongerAvailableMessage'),
        );

        // Trigger navigation away from this room
        if (onDeclined) {
          onDeclined(roomId);
        }
      } else {
        // Generic error for other failures (network issues, etc.)
        showError(
          t('InviteModal.acceptErrorTitle'),
          t('InviteModal.acceptErrorMessage'),
        );
      }
    } finally {
      // Always reset processing state in case the component doesn't unmount promptly.
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    setIsProcessing(true);

    try {
      await client.leave(roomId);
      logger.info(`Successfully declined invite to room ${roomId}`);

      if (onDeclined) {
        onDeclined(roomId);
      }
    } catch (error) {
      logger.error('Failed to decline invite:', error);
      showError(
        t('InviteModal.declineErrorTitle'),
        t('InviteModal.declineErrorMessage'),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeclineAndBlock = async () => {
    if (!inviterUserId) {
      logger.warn('Cannot block invite - inviter user ID not available');
      showError(
        t('InviteModal.blockMissingUserTitle'),
        t('InviteModal.blockMissingUserMessage'),
      );
      return;
    }

    setIsProcessing(true);

    try {
      const normalizedInviterId = inviterUserId.toLowerCase();
      const {
        firstContactDefaultPermission,
        firstContactBlockedList,
        firstContactAllowedList,
      } = settings;

      // Determine which list to modify based on permission mode
      if (firstContactDefaultPermission === 'allow-all') {
        // Case 1: Allow-all mode - add user to blocked list (exception list)
        const isAlreadyBlocked = firstContactBlockedList.some(
          item => item.value === normalizedInviterId && item.type === 'user',
        );

        if (!isAlreadyBlocked) {
          const maxId = (firstContactBlockedList || []).reduce((acc, item) => {
            const id = item?.id;
            return Number.isFinite(id) ? Math.max(acc, id) : acc;
          }, 0);
          const newId = maxId + 1;

          const updatedBlockedList = [
            ...firstContactBlockedList,
            { id: newId, value: normalizedInviterId, type: 'user' },
          ];

          updateSetting('firstContactBlockedList', updatedBlockedList);
          logger.info(`Added ${normalizedInviterId} to blocked list (allow-all mode)`);
        } else {
          logger.info(`${normalizedInviterId} already in blocked list`);
        }
      } else {
        // Case 2: Block-all mode - remove user from allowed list (exception list)
        const updatedAllowedList = firstContactAllowedList.filter(
          item => !(item.value === normalizedInviterId && item.type === 'user'),
        );

        if (updatedAllowedList.length !== firstContactAllowedList.length) {
          updateSetting('firstContactAllowedList', updatedAllowedList);
          logger.info(`Removed ${normalizedInviterId} from allowed list (block-all mode)`);
        } else {
          logger.info(`${normalizedInviterId} not in allowed list`);
        }
      }

      // Now decline the invite. If this fails, we should not claim blocking failed.
      logger.info(`Updated Incoming Invites settings for ${inviterUserId}; now declining invite`);
      try {
        await client.leave(roomId);
        logger.info(`Successfully declined invite to room ${roomId}`);

        if (onDeclined) {
          onDeclined(roomId);
        }
      } catch (leaveError) {
        logger.error('Failed to decline invite after updating block settings:', leaveError);
        showError(
          t('InviteModal.blockSucceededDeclineFailedTitle'),
          t('InviteModal.blockSucceededDeclineFailedMessage'),
        );
      }
    } catch (error) {
      logger.error('Failed to block inviter:', error);
      showError(
        t('InviteModal.blockErrorTitle'),
        t('InviteModal.blockErrorMessage'),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="invite-view">
      <div className="invite-view__content">
        {/* Avatar */}
        <div className="invite-view__avatar-container">
          {inviterAvatarUrl ? (
            <img
              src={inviterAvatarUrl}
              alt={inviterDisplayName}
              className="invite-view__avatar"
            />
          ) : (
            <div className="invite-view__avatar invite-view__avatar--placeholder">
              <ion-icon name="person-outline" />
            </div>
          )}
        </div>

        {/* Invitation message */}
        <div className="invite-view__message">
          <span className="invite-view__inviter-name">{inviterDisplayName}</span>
          {' '}
          {t('InviteModal.hasInvitedYou')}
        </div>

        {/* Room ID */}
        <div className="invite-view__room-id">{roomId}</div>

        {/* Action buttons */}
        <div className="invite-view__actions">
          <div className="invite-view__primary-actions">
            <button
              className="invite-view__button invite-view__button--decline"
              onClick={handleDecline}
              disabled={isProcessing}
              type="button"
            >
              {t('InviteModal.decline')}
            </button>
            <button
              className="invite-view__button invite-view__button--accept"
              onClick={handleAccept}
              disabled={isProcessing}
              type="button"
            >
              {t('InviteModal.accept')}
            </button>
          </div>

          <button
            className="invite-view__button invite-view__button--block"
            onClick={handleDeclineAndBlock}
            disabled={isProcessing}
            type="button"
          >
            {t('InviteModal.declineAndBlock')}
          </button>
        </div>
      </div>
    </div>
  );
};

InviteView.propTypes = {
  roomId: PropTypes.string.isRequired,
  onAccepted: PropTypes.func,
  onDeclined: PropTypes.func,
};

InviteView.defaultProps = {
  onAccepted: null,
  onDeclined: null,
};

export default InviteView;
